import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useMatchDetails } from '../../../shared/hooks/useMatchDetails';
import { useLineups } from '../../../shared/hooks/useLineups';
import { UILineupPlayer, MatchFormation } from '../../../shared/types/database';
import { supabase } from '../../../shared/lib/supabase';

interface PlayerEvent {
  stat_key: string;
  display_name: string;
  match_minute: number;
  event_result: 'success' | 'failure' | 'neutral';
}

interface PlayerEvents {
  goals: number;
  substitutions: number;
  yellowCards: number;
  redCards: number;
  events: PlayerEvent[];
}

// Reusable loading component for consistency
const TabLoader: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex items-center justify-center py-8">
    <div className="flex items-center space-x-2">
      <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      <span className="text-sm text-gray-600">{message}</span>
    </div>
  </div>
);

interface LineupTeamPanelProps {
  teamName: string;
  teamType: 'A' | 'B';
  teamId: string;
  teamLogo?: string;
  squad: UILineupPlayer[];
  formation: MatchFormation | null;
  matchStatus?: 'not_started' | 'live' | 'half_time' | 'full_time';
  matchId: string;
}

interface PlayerCardProps {
  player: UILineupPlayer;
  showCaptainBadge?: boolean;
  compact?: boolean;
  matchStatus?: 'not_started' | 'live' | 'half_time' | 'full_time';
  playerEvents?: PlayerEvents;
}

// Function to fetch player events from aggregated scoresheet data
// Requires team_id to properly filter stats for player in specific team context
const fetchPlayerEvents = async (matchId: string, playerId: string, teamId: string): Promise<PlayerEvents> => {
  try {
    // Fetch aggregated stats from player_scoresheet table
    // Filter by match_id, player_id, AND team_id to get stats for specific team
    const { data: playerStatsData, error } = await supabase
      .from('player_scoresheet')
      .select(`
        value,
        stat_type:stat_types(stat_key, display_name, event_result)
      `)
      .eq('match_id', matchId)
      .eq('player_id', playerId)
      .eq('team_id', teamId); // Filter by team_id to get stats for specific team

    if (error) {
      console.error('Error fetching player events:', error);
      return { goals: 0, substitutions: 0, yellowCards: 0, redCards: 0, events: [] };
    }

    // Extract values from aggregated stats
    let goals = 0;
    let substitutions = 0;
    let yellowCards = 0;
    let redCards = 0;
    const events: PlayerEvent[] = [];

    (playerStatsData || []).forEach((stat: any) => {
      const statKey = stat.stat_type?.stat_key;
      const value = stat.value || 0;

      if (!statKey) return;

      // Count cards from aggregated values
      if (statKey === 'yellow_card') {
        yellowCards = value;
      } else if (statKey === 'red_card') {
        redCards = value;
      } else if (statKey === 'total_goals') {
        goals = value;
      } else if (statKey === 'substitution_in' || statKey === 'substitution_out') {
        substitutions += value;
      }

      // Add to events array for detailed view (if needed)
      events.push({
        stat_key: statKey,
        display_name: stat.stat_type?.display_name || '',
        match_minute: 0, // Not available in aggregated data
        event_result: stat.stat_type?.event_result || 'neutral'
      });
    });

    // If total_goals not found, calculate from goal-related source stats
    if (goals === 0) {
      const goalStats = (playerStatsData || []).filter((s: any) => {
        const statKey = s.stat_type?.stat_key;
        return statKey && (
          statKey === 'shot_inside_box_goal' ||
          statKey === 'shot_outside_box_goal' ||
          statKey === 'penalty_scored' ||
          statKey === 'own_goal'
        );
      });
      if (goalStats.length > 0) {
        goals = goalStats.reduce((sum: number, stat: any) => sum + (stat.value || 0), 0);
      }
    }

    return { goals, substitutions, yellowCards, redCards, events };
  } catch (error) {
    console.error('Error fetching player events:', error);
    return { goals: 0, substitutions: 0, yellowCards: 0, redCards: 0, events: [] };
  }
};

const PlayerCard: React.FC<PlayerCardProps> = ({ 
  player, 
  showCaptainBadge = false,
  compact = false,
  matchStatus = 'not_started',
  playerEvents
}) => {
  return (
    <div className={`bg-white border border-gray-200 rounded-lg p-3 ${compact ? 'py-2' : ''}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
            <span className="text-xs font-semibold text-gray-600">
              {player.kit_number || '?'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-gray-900 truncate">
                {player.player?.full_name || 'Unknown Player'}
              </span>
              {showCaptainBadge && player.isCaptain && (
                <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full font-medium">
                  C
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500">
              {player.player?.position || 'Unknown Position'}
            </div>
          </div>
        </div>
        
        {/* Match events indicators */}
        <div className="flex items-center space-x-1">
          {playerEvents && playerEvents.goals > 0 && (
            <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium">
              ⚽ {playerEvents.goals}
            </span>
          )}
          {playerEvents && playerEvents.yellowCards > 0 && (
            <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full font-medium">
              🟨 {playerEvents.yellowCards}
            </span>
          )}
          {playerEvents && playerEvents.redCards > 0 && (
            <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full font-medium">
              🟥 {playerEvents.redCards}
            </span>
          )}
          {playerEvents && playerEvents.substitutions > 0 && (
            <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full font-medium">
              🔄 {playerEvents.substitutions}
            </span>
          )}
          {(!playerEvents || (playerEvents.goals === 0 && playerEvents.yellowCards === 0 && playerEvents.redCards === 0 && playerEvents.substitutions === 0)) && (
            <span className="text-xs text-gray-400">
              No events
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const LineupTeamPanel: React.FC<LineupTeamPanelProps> = ({ 
  teamName, 
  teamType, 
  teamId,
  teamLogo,
  squad, 
  formation, 
  matchStatus = 'not_started',
  matchId
}) => {
  const [playerEventsMap, setPlayerEventsMap] = useState<Map<string, PlayerEvents>>(new Map());

  const startingPlayers = squad.filter(player => player.isStarting);
  const benchPlayers = squad.filter(player => player.isBench);

  // Fetch player events for all players
  useEffect(() => {
    const fetchAllPlayerEvents = async () => {
      const eventsMap = new Map<string, PlayerEvents>();
      
      // Fetch events for all players in parallel
      // Use composite key (player_id + team_id) to handle same player in multiple teams
      const promises = squad.map(async (player) => {
        const compositeKey = `${player.player_id}-${player.team_id}`;
        const events = await fetchPlayerEvents(matchId, player.player_id, player.team_id);
        eventsMap.set(compositeKey, events);
      });
      
      await Promise.all(promises);
      setPlayerEventsMap(eventsMap);
    };

    if (matchId && squad.length > 0) {
      fetchAllPlayerEvents();
    }
  }, [matchId, squad]);
  
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex-1 min-w-0">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center overflow-hidden">
            {teamLogo ? (
              <img 
                src={teamLogo} 
                alt={`${teamName} logo`} 
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
            ) : null}
            <span className={`text-gray-600 text-xs font-bold ${teamLogo ? 'hidden' : ''}`}>
              {teamName.charAt(0)}
            </span>
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-800">{teamName}</h3>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Manager and Formation - Read Only */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Manager</label>
            <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm text-gray-900">
              {formation?.manager_name || 'Not specified'}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Formation</label>
            <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm text-gray-900">
              {formation?.formation || 'Not specified'}
            </div>
          </div>
        </div>

        {/* Starting Lineup */}
        <div>
          <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
            <span className="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
            Starting Lineup ({startingPlayers.length})
          </h4>
          <div className="space-y-1">
            {startingPlayers.length === 0 ? (
              <div className="text-center py-6 text-gray-500 text-sm bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                No starting lineup selected
              </div>
            ) : (
              startingPlayers.map((player) => {
                // Use composite key (player_id + team_id) to handle same player in multiple teams
                const compositeKey = `${player.player_id}-${player.team_id}`;
                return (
                  <PlayerCard
                    key={compositeKey}
                    player={player}
                    showCaptainBadge={true}
                    matchStatus={matchStatus}
                    playerEvents={playerEventsMap.get(compositeKey)}
                  />
                );
              })
            )}
          </div>
        </div>

        {/* Bench Players */}
        <div>
          <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
            <span className="w-3 h-3 bg-orange-500 rounded-full mr-2"></span>
            On Bench ({benchPlayers.length})
          </h4>
          <div className="space-y-1">
            {benchPlayers.length === 0 ? (
              <div className="text-center py-6 text-gray-500 text-sm bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                No bench players selected
              </div>
            ) : (
              benchPlayers.map((player) => {
                // Use composite key (player_id + team_id) to handle same player in multiple teams
                const compositeKey = `${player.player_id}-${player.team_id}`;
                return (
                  <PlayerCard
                    key={compositeKey}
                    player={player}
                    showCaptainBadge={true}
                    matchStatus={matchStatus}
                    playerEvents={playerEventsMap.get(compositeKey)}
                  />
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const LineupTab: React.FC = () => {
  const location = useLocation();
  
  // Extract the actual match ID from the URL path
  const extractMatchIdFromPath = () => {
    const path = location.pathname;
    // Handle both old format (with -match-center) and new format (clean)
    const match = path.match(/\/reports\/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})(?:-match-center)?/);
    return match ? match[1] : '';
  };

  const matchId = extractMatchIdFromPath();
  
  // Debug logging to help troubleshoot
  console.log('LineupTab Debug:', {
    pathname: location.pathname,
    extractedMatchId: matchId,
    regexMatch: location.pathname.match(/\/reports\/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})(?:-match-center)?/)
  });
  
  const { matchDetails, loading: matchLoading, error: matchError } = useMatchDetails(matchId);

  const teamAId = matchDetails?.home_team_id;
  const teamBId = matchDetails?.away_team_id;

  // Match status - simplified without timeline integration
  const [matchStatus] = useState<'not_started' | 'live' | 'half_time' | 'full_time'>('not_started');

  const {
    teamASquad,
    teamBSquad,
    teamAFormation,
    teamBFormation,
    loading: lineupLoading,
    error: lineupError,
  } = useLineups(matchId, teamAId, teamBId, matchDetails?.tournament_id);

  if (matchLoading || lineupLoading) {
    return <TabLoader message="Loading lineup data..." />;
  }

  if (matchError || lineupError) {
    return <div className="text-red-500 p-4">Error: {matchError || lineupError}</div>;
  }

  if (!matchId || !matchDetails || !teamAId || !teamBId) {
    return <div className="text-red-500 p-4">Error: No match ID provided</div>;
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-col lg:flex-row gap-4">
        <LineupTeamPanel
          teamName={matchDetails?.home_team?.name || 'Team A'}
          teamType="A"
          teamId={teamAId || ''}
          teamLogo={matchDetails?.home_team?.logo}
          squad={teamASquad}
          formation={teamAFormation}
          matchStatus={matchStatus}
          matchId={matchId}
        />
        <LineupTeamPanel
          teamName={matchDetails?.away_team?.name || 'Team B'}
          teamType="B"
          teamId={teamBId || ''}
          teamLogo={matchDetails?.away_team?.logo}
          squad={teamBSquad}
          formation={teamBFormation}
          matchStatus={matchStatus}
          matchId={matchId}
        />
      </div>
    </div>
  );
};

export default LineupTab;